<script setup lang="ts">
import {useTemplateRef} from "vue";
import {QInput} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import type {MenuItem} from "./useQuestionList.ts";

const props = defineProps<{
  items: MenuItem[],
  isLoading: boolean,
  searchText: string,
  selectAllState: boolean | null,
  isItemSelected: (item: MenuItem) => boolean,
  fieldTitle: string,
  selectedRowsNumber?: number,
}>()

const emit = defineEmits<{
  (e: 'update:searchText', value: string): void,
  (e: 'toggleAll'): void,
  (e: 'toggleItem', item: MenuItem): void,
  (e: 'pasteText', text: string): void,
  (e: 'copySelected'): void,
}>()

const searchRef = useTemplateRef<QInput>("searchRef");

defineExpose({focus: () => searchRef.value?.focus()});

function onSearchPaste(event: ClipboardEvent) {
  const text = event.clipboardData?.getData('text') ?? '';
  if (/\r|\n/.test(text)) {
    event.preventDefault();
    emit('pasteText', text);
  }
}

async function onPasteClick() {
  try {
    const text = await navigator.clipboard.readText();
    emit('pasteText', text);
  } catch {
    searchRef.value?.focus();
  }
}
</script>

<template>
  <div class="tw-flex tw-flex-col">
    <div class="tw-sticky tw-top-0 tw-z-10 tw-p-2 tw-border-b tw-border-black/10 dark:tw-border-white/10
                tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark">
      <q-input ref="searchRef"
               :model-value="searchText"
               @update:model-value="value => emit('update:searchText', (value ?? '') + '')"
               @paste="onSearchPaste"
               dense outlined
               clearable
               autofocus
               :placeholder="Translator.translateId('search')"
               :loading="isLoading"
               class="nama-customize-dense-input nama-focused-input nama-field-input"
               input-class="nama-input-class">
        <template v-slot:prepend>
          <q-avatar square>
            <q-icon name="search" size="18px"/>
          </q-avatar>
        </template>
        <template v-slot:append>
          <q-icon name="content_paste" size="18px" class="tw-cursor-pointer"
                  :title="Translator.translateId('paste')" @click.stop.prevent="onPasteClick"/>
          <q-icon name="content_copy" size="18px"
                  :class="props.selectedRowsNumber ? 'tw-cursor-pointer' : 'tw-opacity-40 tw-cursor-not-allowed'"
                  :title="Translator.translateId('copySelectedRows')"
                  @click.stop.prevent="props.selectedRowsNumber && emit('copySelected')"/>
        </template>
      </q-input>
    </div>
    <div class="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1 tw-border-b tw-border-black/5 dark:tw-border-white/10
                tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-black/45 dark:tw-text-white/45">
        <q-item-section side class="!tw-min-w-0">
          <q-checkbox
            :dark="useNamaFlagsStore().darkMode"
            :model-value="selectAllState"
            :indeterminate-value="null"
            :toggle-indeterminate="false"
            @click.stop.prevent="emit('toggleAll')"
            dense size="sm"
            class="tw-shrink-0"/>
        </q-item-section>
        <q-item-section class="!tw-max-w-[90px] !tw-min-w-[90px]">
          <q-item-label class="tw-text-xs tw-font-semibold tw-truncate" v-text="Translator.translateId('code')"/>
        </q-item-section>
        <q-item-section>
          <q-item-label class="tw-text-xs tw-text-black/70 dark:tw-text-white/70 tw-truncate" v-text="fieldTitle"/>
        </q-item-section>
    </div>
    <q-list dense class="tw-overflow-y-auto tw-min-w-[250px] tw-w-full tw-h-fit">
      <q-item
        v-for="item in items"
        :key="item.id"
        clickable
        @click="emit('toggleItem', item)">
        <q-item-section side class="!tw-min-w-0">
          <q-checkbox
            :dark="useNamaFlagsStore().darkMode"
            :model-value="isItemSelected(item)"
            @click.stop.prevent="emit('toggleItem', item)"
            dense size="sm"/>
        </q-item-section>
        <q-item-section class="!tw-max-w-[90px] !tw-min-w-[90px]">
          <q-item-label class="tw-text-xs tw-font-semibold tw-truncate" v-text="item.code"/>
        </q-item-section>
        <q-item-section>
          <q-item-label class="tw-text-xs tw-text-black/70 dark:tw-text-white/70 tw-truncate" v-text="item.caption"/>
        </q-item-section>
      </q-item>
      <q-item v-if="!items.length && !isLoading">
        <q-item-section>
          <q-item-label class="dark:tw-text-white/50" caption v-text="Translator.translateId('noResults')"/>
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>
